/* ----------------------------------------------------------------------------
 * AuraMicOnboardingCard — the signup / first-run "How should Aura listen?" card
 * (owner directive 2026-07-24). A dismissible invitation shown ONLY when the
 * account is still on the safe default ('standard') and voice is available on
 * this environment. Choosing Continuous/Both here runs the same consent +
 * device-permission gesture as Settings and arms this device immediately.
 *
 * Self-contained + low-footprint: renders null in the common case (already
 * chosen, already dismissed, or voice unavailable), so the dashboard is
 * unaffected for everyone who has made a choice.
 * -------------------------------------------------------------------------- */

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getVoiceStatus } from '../net/voiceApi.ts';
import { useHandsFreeContext } from './HandsFreeProvider.tsx';
import { AuraMicModeControl } from './AuraMicModeControl.tsx';

const DISMISS_KEY = 'aura.mic.onboard.dismissed.v1';

function readDismissed(): boolean {
  try { return localStorage.getItem(DISMISS_KEY) === '1'; } catch { return false; }
}

export function AuraMicOnboardingCard() {
  const { micMode } = useHandsFreeContext();
  const [dismissed, setDismissed] = useState<boolean>(readDismissed);

  const voiceQuery = useQuery({
    queryKey: ['voice-status'],
    queryFn: () => getVoiceStatus(),
    retry: false,
    staleTime: 60_000,
  });
  const voiceAvailable = voiceQuery.data?.voice_available === true;

  // Show only for a fresh account (standard) on a voice-capable environment.
  if (dismissed || micMode !== 'standard' || !voiceAvailable) return null;

  function dismiss() {
    try { localStorage.setItem(DISMISS_KEY, '1'); } catch { /* private mode */ }
    setDismissed(true);
  }

  return (
    <section
      aria-label="Set up Aura hands-free"
      className="rounded-2xl border border-ai/30 bg-surface p-5 shadow-ai"
    >
      <div className="mb-1 flex items-center justify-between gap-3">
        <h2 className="text-sm font-semibold text-text-primary">How should Aura listen?</h2>
        <button
          type="button"
          onClick={dismiss}
          className="text-[11px] text-text-tertiary underline-offset-2 hover:underline"
        >
          Maybe later
        </button>
      </div>
      <p className="mb-3 max-w-[60ch] text-[13px] leading-relaxed text-text-secondary">
        Aura can wait for you to press-to-talk, or listen hands-free for “Hey Aura” on any screen — your
        choice, and you can change it any time in Settings.
      </p>
      <AuraMicModeControl variant="onboarding" onChanged={(mode) => { if (mode !== 'standard') dismiss(); }} />
    </section>
  );
}
